import React, { useState, useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import { Save, Plus, Trash2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, addDoc, deleteDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '../firebase';
import Swal from 'sweetalert2';
import { fetchSistemaInventory } from '../api/sistemaBridge';

const ProfitabilityView = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const UMBRAL_RENTABILIDAD = 0.15;

    useEffect(() => {
        const q = query(collection(db, 'inventario_cuentas'), orderBy('servicio', 'asc'), limit(1000));
        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setItems(data);
            setLoading(false);
        }, (error) => {
            console.error('Se perdió la conexión en tiempo real:', error);
            setLoading(false);
            Swal.fire('Error', 'Se perdió la conexión en tiempo real', 'error');
        });
        return unsub;
    }, []);

    const getMargenStyle = (margen) => {
        if (margen < 0) return 'text-red-400 bg-red-500/10 rounded px-1';
        if (margen <= UMBRAL_RENTABILIDAD) return 'text-amber-400 bg-yellow-400/10 rounded px-1';
        return 'text-emerald-400 bg-emerald-500/10 rounded px-1';
    };

    const handleLocalChange = (id, field, value) => {
        const newItems = items.map(item => {
            if (item.id === id) {
                const numValue = Number(value) || 0;
                let updatedItem = { ...item, [field]: numValue };
                
                // Si cambia el Costo Base, recalculamos automáticamente el 35% de Asociados como sugerencia inicial
                if (field === 'costo_proveedor') {
                    updatedItem.costos_asociados = Number((numValue * 0.35).toFixed(2));
                }
                
                return updatedItem;
            }
            return item;
        });
        setItems(newItems);
        setHasChanges(true);
    };

    const saveChanges = async () => {
        try {
            Swal.fire({ title: 'Sincronizando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            
            const promises = items.map(item => 
                updateDoc(doc(db, 'inventario_cuentas', item.id), {
                    costo_proveedor: Number(item.costo_proveedor) || 0,
                    costos_asociados: Number(item.costos_asociados) || 0,
                    precio_distribuidor: Number(item.precio_distribuidor) || 0,
                    precio_revendedor: Number(item.precio_revendedor) || 0,
                    precio_cliente_final: Number(item.precio_cliente_final) || 0,
                    updatedAt: serverTimestamp()
                })
            );

            await Promise.all(promises);
            setHasChanges(false);
            Swal.fire({ title: '¡Éxito!', text: 'Estructura de costos actualizada en la nube', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            Swal.fire('Error', 'No se pudieron persistir los cambios', 'error');
        }
    };

    const syncSistemaInventory = async () => {
        try {
            Swal.fire({ title: 'Sincronizando Sistema...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const inventory = await fetchSistemaInventory();
            const services = [...(inventory.stock_perfiles || []), ...(inventory.stock_cuentas || [])];
            const existing = new Set(items.map(item => String(item.servicio || '').trim().toUpperCase()));
            const newServices = services.filter(service => {
                const name = String(service.servicio || service.plataforma || '').trim();
                return name && !existing.has(name.toUpperCase());
            });
            await Promise.all(newServices.map(service => addDoc(collection(db, 'inventario_cuentas'), {
                servicio: service.servicio || service.plataforma,
                costo_proveedor: 0,
                costos_asociados: 0,
                precio_distribuidor: 0,
                precio_revendedor: 0,
                precio_cliente_final: 0,
                origen: 'Sistema',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            })));
            Swal.fire('Sincronización completada', `${newServices.length} servicio(s) nuevo(s) incorporado(s) sin duplicados.`, 'success');
        } catch (error) {
            Swal.fire('Error', error.message || 'No se pudo sincronizar Sistema.', 'error');
        }
    };

    const addNewService = async () => {

        const { value: formValues } = await Swal.fire({
            title: 'Nuevo Servicio (Análisis)',
            background: '#0f172a',
            color: '#f8fafc',
            html:
                '<input id="swal-servicio" class="swal2-input bg-slate-900 text-white border-slate-700" placeholder="Nombre del Servicio">' +
                '<input id="swal-correo" class="swal2-input bg-slate-900 text-white border-slate-700" placeholder="Correo / ID">',
            focusConfirm: false,
            preConfirm: () => {
                return [
                    document.getElementById('swal-servicio').value,
                    document.getElementById('swal-correo').value
                ]
            }
        });

        if (formValues && formValues[0]) {
            try {
                await addDoc(collection(db, 'inventario_cuentas'), {
                    servicio: formValues[0],
                    correo_cuenta: formValues[1] || 'Análisis Interno',
                    costo_proveedor: 0,
                    costos_asociados: 0,
                    precio_distribuidor: 0,
                    precio_revendedor: 0,
                    precio_cliente_final: 0,
                    estado: 'Disponible',
                    fecha_registro: serverTimestamp()
                });
                Swal.fire({ title: 'Creado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } catch (err) {
                Swal.fire('Error', 'No se pudo crear el servicio', 'error');
            }
        }
    };

    const deleteService = async (id, name) => {
        const result = await Swal.fire({
            title: '¿Eliminar Servicio?',
            text: `Esta acción borrará ${name} de forma permanente.`,
            icon: 'warning',
            background: '#0f172a',
            color: '#f8fafc',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#334155',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'inventario_cuentas', id));
                Swal.fire({ title: 'Eliminado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } catch (err) {
                Swal.fire('Error', 'No se pudo eliminar el servicio', 'error');
            }
        }
    };

    return (
        <div className="min-h-screen">
            <PageHeader
                title="Rentabilidad"
                subtitle="Gestión Avanzada de Márgenes y Costos (Hoprin+)"
                action={
                    <div className="flex gap-2">
                        <button 
                            onClick={syncSistemaInventory}
                            className="bg-slate-800 hover:bg-slate-700 text-emerald-400 border border-emerald-500/20 px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-lg"
                        >
                            <Plus size={18} /> <span className="hidden sm:inline text-sm font-bold uppercase tracking-wider">Nuevo</span>
                        </button>
                        <button 
                            onClick={saveChanges}
                            disabled={!hasChanges}
                            className={`${hasChanges ? 'bg-indigo-600 hover:bg-indigo-500 text-white animate-pulse' : 'bg-slate-800 text-slate-500 border-slate-700 cursor-not-allowed'} px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-lg border border-transparent`}
                        >
                            <Save size={18} /> <span className="hidden sm:inline text-sm font-bold uppercase tracking-wider">Sincronizar</span>
                        </button>
                    </div>
                }
            />

            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden animate-in fade-in duration-500">
                <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                        <thead className="bg-slate-900/60 text-slate-400 uppercase text-[10px] tracking-widest font-bold">
                            <tr>
                                <th className="px-4 py-4 border-b border-slate-700/50">ID / Descripción</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">Costo Base ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">Asociados ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center font-bold text-cyan-400">Total Unit. ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">P. Dist ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">P. Rev ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">P. Cli ($)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">MB Dist (%)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">MB Rev (%)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">MB Cli (%)</th>
                                <th className="px-4 py-4 border-b border-slate-700/50 text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30">
                            {loading ? (
                                <tr><td colSpan="11" className="text-center py-20 text-slate-500 animate-pulse font-bold">Sincronizando datos maestros...</td></tr>
                            ) : items.map((item) => {
                                const cBase = Number(item.costo_proveedor || 0);
                                const cAsoc = Number(item.costos_asociados || 0);
                                const cTotal = cBase + cAsoc;

                                const pDist = Number(item.precio_distribuidor || 0);
                                const pRev = Number(item.precio_revendedor || 0);
                                const pCli = Number(item.precio_cliente_final || 0);

                                const mbDist = pDist > 0 ? (pDist - cTotal) / pDist : 0;
                                const mbRev = pRev > 0 ? (pRev - cTotal) / pRev : 0;
                                const mbCli = pCli > 0 ? (pCli - cTotal) / pCli : 0;

                                return (
                                    <tr key={item.id} className="hover:bg-slate-700/20 transition-colors group">
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="text-white font-bold text-xs">{item.servicio}</span>
                                                <span className="text-slate-500 text-[10px] font-mono tracking-tight">{item.correo_cuenta}</span>
                                            </div>
                                        </td>
                                        
                                        <td className="px-4 py-3 text-center">
                                            <input 
                                                type="number" step="0.01" value={item.costo_proveedor} 
                                                onChange={(e) => handleLocalChange(item.id, 'costo_proveedor', e.target.value)}
                                                className="w-16 bg-slate-900/50 border border-slate-700/50 rounded px-1.5 py-1 text-center font-mono text-[11px] text-white focus:border-cyan-500 focus:outline-none"
                                            />
                                        </td>
                                        
                                        <td className="px-4 py-3 text-center">
                                            <input 
                                                type="number" step="0.01" value={item.costos_asociados} 
                                                onChange={(e) => handleLocalChange(item.id, 'costos_asociados', e.target.value)}
                                                className="w-16 bg-slate-700/30 border border-slate-600/30 rounded px-1.5 py-1 text-center font-mono text-[11px] text-amber-400 focus:border-amber-500 focus:outline-none"
                                            />
                                        </td>

                                        <td className="px-4 py-3 text-center font-mono text-[11px] font-bold text-cyan-400 bg-cyan-500/5 border-x border-cyan-500/10">
                                            ${cTotal.toFixed(2)}
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                            <input 
                                                type="number" step="0.01" value={item.precio_distribuidor} 
                                                onChange={(e) => handleLocalChange(item.id, 'precio_distribuidor', e.target.value)}
                                                className="w-16 bg-slate-900/50 border border-slate-700/50 rounded px-1.5 py-1 text-center font-mono text-[11px] text-white focus:border-indigo-500 focus:outline-none"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <input 
                                                type="number" step="0.01" value={item.precio_revendedor} 
                                                onChange={(e) => handleLocalChange(item.id, 'precio_revendedor', e.target.value)}
                                                className="w-16 bg-slate-900/50 border border-slate-700/50 rounded px-1.5 py-1 text-center font-mono text-[11px] text-white focus:border-indigo-500 focus:outline-none"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <input 
                                                type="number" step="0.01" value={item.precio_cliente_final} 
                                                onChange={(e) => handleLocalChange(item.id, 'precio_cliente_final', e.target.value)}
                                                className="w-16 bg-slate-900/50 border border-slate-700/50 rounded px-1.5 py-1 text-center font-mono text-[11px] text-white focus:border-indigo-500 focus:outline-none"
                                            />
                                        </td>
                                        
                                        <td className="px-4 py-3 text-center font-mono text-[11px] font-bold">
                                            <div className={`flex items-center justify-center gap-1 ${getMargenStyle(mbDist)}`}>
                                                {(mbDist * 100).toFixed(1)}%
                                                {mbDist > UMBRAL_RENTABILIDAD ? <TrendingUp size={12} /> : mbDist < 0 ? <TrendingDown size={12} /> : null}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center font-mono text-[11px] font-bold">
                                            <div className={`flex items-center justify-center gap-1 ${getMargenStyle(mbRev)}`}>
                                                {(mbRev * 100).toFixed(1)}%
                                                {mbRev > UMBRAL_RENTABILIDAD ? <TrendingUp size={12} /> : mbRev < 0 ? <TrendingDown size={12} /> : null}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center font-mono text-[11px] font-bold">
                                            <div className={`flex items-center justify-center gap-1 ${getMargenStyle(mbCli)}`}>
                                                {(mbCli * 100).toFixed(1)}%
                                                {mbCli > UMBRAL_RENTABILIDAD ? <TrendingUp size={12} /> : mbCli < 0 ? <TrendingDown size={12} /> : null}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => deleteService(item.id, item.servicio)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all">
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 bg-slate-900/40 border-t border-slate-700/50 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2">
                        <DollarSign size={14} className="text-amber-500" />
                        <p className="text-[10px] text-slate-400 font-medium">Los Asociados se sugieren al 35% pero son editables manualmente.</p>
                    </div>
                    <div className="flex gap-4">
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pérdida</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Justo</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Saludable</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {hasChanges && (
                <button 
                    onClick={saveChanges}
                    className="md:hidden fixed bottom-24 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-2xl z-50 animate-bounce flex items-center justify-center"
                >
                    <Save size={24} />
                </button>
            )}
        </div>
    );
};

export default ProfitabilityView;
